// Tree sync — pulls each tree from Supabase on boot and debounces pushes
// after edits. The local tree (state + localStorage) remains the source of
// truth for the UI; this module only mirrors it to Postgres for cross-device
// persistence and as a backup.
//
// Conflict model: timestamp-based, last-write-wins per tree. We track the
// last successful sync time per storage key in localStorage; on boot, if the
// remote `updated_at` is newer than that and the data differs from local, we
// surface a prompt asking the user which copy to keep.

import { supabase, supabaseConfigured } from '../config/supabaseClient';

const PUSH_DEBOUNCE_MS = 2000;
const SYNC_TS_PREFIX = 'gia-pha-cloud-sync-';

export const cloudSyncConfigured = supabaseConfigured;

const pushTimers = new Map();
const pushPending = new Map(); // storageKey -> latest tree payload to push

function lastSyncStorageKey(storageKey) {
  return SYNC_TS_PREFIX + storageKey;
}

export function getLastSyncTs(storageKey) {
  const v = localStorage.getItem(lastSyncStorageKey(storageKey));
  return v ? new Date(v).getTime() : 0;
}

export function setLastSyncTs(storageKey, isoOrEpoch) {
  const iso =
    typeof isoOrEpoch === 'number'
      ? new Date(isoOrEpoch).toISOString()
      : isoOrEpoch;
  localStorage.setItem(lastSyncStorageKey(storageKey), iso);
}

export async function pullTree(storageKey) {
  if (!supabaseConfigured) return null;
  const { data, error } = await supabase
    .from('trees')
    .select('data, updated_at')
    .eq('storage_key', storageKey)
    .maybeSingle();
  if (error) {
    console.warn('[gia-pha] cloud pull failed:', error.message || error);
    return null;
  }
  return data ? { data: data.data, updatedAt: data.updated_at } : null;
}

async function actualPush(storageKey) {
  if (!supabaseConfigured) {
    console.warn('[gia-pha] cloud push skipped: supabase not configured');
    return { ok: false };
  }

  // Don't push without a confirmed session — prevents 401s during auth
  // establishment that can trigger spurious SIGNED_OUT events.
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) {
    console.warn('[gia-pha] cloud push skipped: no session');
    return { ok: false };
  }

  const treeData = pushPending.get(storageKey);
  if (treeData === undefined) return { ok: true };
  pushPending.delete(storageKey);
  console.log('[gia-pha] cloud push start:', storageKey);
  const { data, error } = await supabase
    .from('trees')
    .upsert({ storage_key: storageKey, data: treeData })
    .select('updated_at')
    .single();
  if (error) {
    console.warn('[gia-pha] cloud push FAILED:', storageKey, error.message || error);
    // Re-queue so the next edit (or flush) retries — don't drop the payload.
    if (!pushPending.has(storageKey)) pushPending.set(storageKey, treeData);
    return { ok: false, error };
  }
  console.log('[gia-pha] cloud push OK:', storageKey, '@', data.updated_at);
  setLastSyncTs(storageKey, data.updated_at);
  return { ok: true, updatedAt: data.updated_at };
}

export function schedulePush(storageKey, treeData) {
  if (!supabaseConfigured) return;
  pushPending.set(storageKey, treeData);
  if (pushTimers.has(storageKey)) clearTimeout(pushTimers.get(storageKey));
  pushTimers.set(
    storageKey,
    setTimeout(() => {
      pushTimers.delete(storageKey);
      actualPush(storageKey);
    }, PUSH_DEBOUNCE_MS)
  );
}

// Force any pending debounced push to fire immediately. Useful when the user
// resolves a conflict by keeping their local copy — we want the override to
// land on the server right away rather than wait for the debounce window.
export async function flushPush(storageKey) {
  if (pushTimers.has(storageKey)) {
    clearTimeout(pushTimers.get(storageKey));
    pushTimers.delete(storageKey);
  }
  return actualPush(storageKey);
}
